import './Image.scss';
import L, { Point } from 'leaflet';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode } from 'react';
import { detect } from 'un-detector';
import { Loader } from '../../../components/Loader';
import { ProgressBar } from '../../../components/ProgressBar';
import { scaleToZoom, zoomToScale } from '../../../helpers/geometry/mapExtensions.helpers';
import { MOUSE_BUTTONS } from '../annotations.interface';
import { CustomImageOverlay } from './CustomImageOverlay';
import { CustomCRS } from './DrawControl.models/CustomCRS';
import { MapEx } from './DrawControl.models/MapEx';

export interface IImageComponentProps {
  height: number;
  width: number;
  url: string;
  lowQualityUrl: string;
  isLoading: boolean;
  isHighResImageLoading: boolean;
  zoomLevel: number;
  minZoomLevel: number;
  maxZoomLevel: number;
  isLeftMenuOpened: boolean;
  disableZoom?: boolean;
  onZoomChanged(zoom: number): void;
  onLowResImageLoaded(): void;
  heatmapUrl?: string | null;
  maskUrl?: string | null;
  children?: ReactNode;
}

export const Image = observer((props: IImageComponentProps): ReactElement => {
  const handleLowResOverlayLoad = (event: L.LayerEvent): void => {
    const scale = event.target._map._zoom as number;
    props.onZoomChanged(scaleToZoom(scale));
    props.onLowResImageLoaded();
  };

  const handleMapZoom = (event: L.LeafletEvent): void => {
    const scale = event.target._zoom as number;
    props.onZoomChanged(scaleToZoom(scale));
  };

  const detectedBrowser = detect(window.navigator.userAgent);

  let imageClassName = '';
  if (props.zoomLevel > 100) {
    if (detectedBrowser.is.firefox) {
      imageClassName = 'crisp-edges';
    } else {
      imageClassName = 'pixelated';
    }
  }

  return (
    <div className='map-wrapper'>
      <Loader isLoading={props.isLoading}>
        <MapEx
          imageCenterPaddingTopLeft={props.isLeftMenuOpened ? new Point(324, 0) : undefined}
          keyboard={false}
          drawControlTooltips={false}
          editable={true}
          draggingButtons={MOUSE_BUTTONS.MIDDLE}
          zoomAnimation={false}
          boxZoom={false}
          fadeAnimation={false}
          animate={false}
          duration={0}
          crs={CustomCRS}
          center={[0, 0]}
          zoomControl={false}
          zoom={zoomToScale(props.zoomLevel)}
          minZoom={zoomToScale(props.minZoomLevel)}
          maxZoom={zoomToScale(props.maxZoomLevel)}
          zoomSnap={0.001}
          onzoomend={handleMapZoom}
          bounds={[
            [-18, -18],
            [props.height + 18, props.width + 18],
          ]}
          maxBounds={[
            [-props.height, -props.width],
            [props.height * 2, props.width * 2],
          ]}
          doubleClickZoom={false}
          scrollWheelZoom={!props.disableZoom}
          touchZoom={!props.disableZoom}
          attributionControl={false}
        >
          {!props.isLoading && props.isHighResImageLoading && (
            <ProgressBar
              enableStripesAnimation
              value={100}
            />
          )}
          {props.lowQualityUrl !== '' && (
            <CustomImageOverlay
              url={props.lowQualityUrl}
              zIndex={2}
              bounds={[
                [0, 0],
                [props.height, props.width],
              ]}
              onload={handleLowResOverlayLoad}
            />
          )}
          {props.url !== '' && (
            <CustomImageOverlay
              className={imageClassName}
              url={props.url}
              zIndex={3}
              bounds={[
                [0, 0],
                [props.height, props.width],
              ]}
              onload={handleLowResOverlayLoad}
            />
          )}
          {props.heatmapUrl && (
            <CustomImageOverlay
              className={imageClassName}
              url={props.heatmapUrl}
              zIndex={4}
              bounds={[
                [0, 0],
                [props.height, props.width],
              ]}
              onload={handleLowResOverlayLoad}
            />
          )}
          {props.maskUrl && (
            <CustomImageOverlay
              className={imageClassName}
              url={props.maskUrl}
              zIndex={5}
              bounds={[
                [0, 0],
                [props.height, props.width],
              ]}
              onload={handleLowResOverlayLoad}
            />
          )}
          {props.children}
        </MapEx>
      </Loader>
    </div>
  );
});
